"use client";

import styles from "@/components/pages/beats/beats-page.module.scss";
import { useBeats } from "@/components/pages/beats/hooks/use-beats";
import BeatSetting from "@/components/pages/beats/components/beat-setting";
import BpmSettingHeader from "@/components/pages/beats/components/setting-headers/bpm-setting-header";
import EffectSettingHeader from "@/components/pages/beats/components/setting-headers/effect-setting-header";
import FilledButton from "@/components/common/filled-button";
import EffectsContainerHeader from "@/components/pages/beats/components/setting-headers/effects-container-header";
import BeatResult from "@/components/pages/beats/components/beat-result";
import MediaFormatsInput from "@/components/common/input/media-formats-input";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import ServicePageHeader from "@/components/common/service-page-header";
import { useGlobalModalContext } from "@/providers/modal-provider/modal-provider";

const BeatsPage = () => {
  const {
    isLoading,
    resultFile,
    downloadResultFile,
    bpmSettings,
    setIsManualTempo,
    setBpm,
    setTolerance,
    effects,
    canAddNewEffect,
    canRemoveLastEffect,
    canStartApplyingEffects,
    addNewEffect,
    removeEffect,
    changeEffectType,
    updateEffectParameterValue,
    applyEffects,
  } = useBeats();

  const { showModal } = useGlobalModalContext();

  const { license } = useProfileContext();

  const showLicenseLimitModal = () =>
    showModal({
      type: "license_limited_modal",
      props: {
        licenseName: license,
        allowsMessage: ``,
        upgradeToMessage: "use beat machine",
      },
    });

  const isAvailableForUser = license !== "Trial" && license !== "Starter";

  const bpmSettingHeader = (
    <BpmSettingHeader
      isManualTempo={bpmSettings.isManualTempo}
      setIsManualTempo={setIsManualTempo}
    />
  );

  const bpmSettingValidationErrorMessages: string[] = [];

  if (!bpmSettings.isBpmValid && bpmSettings.bpmValidationMessage) {
    bpmSettingValidationErrorMessages.push(bpmSettings.bpmValidationMessage);
  }

  if (!bpmSettings.isToleranceValid && bpmSettings.toleranceValidationMessage) {
    bpmSettingValidationErrorMessages.push(
      bpmSettings.toleranceValidationMessage,
    );
  }

  return (
    <div className={styles.page_wrapper}>
      <div className={styles.uploader_wrapper}>
        <ServicePageHeader
          title={
            <div>
              Beat <span>Machine</span>
            </div>
          }
          description={
            "Make every other beat is missing remixes right in your browser and many other kinds of beat edits, too. Choose a song, stack some effects, and hear the results."
          }
        ></ServicePageHeader>
        <MediaFormatsInput
          localFileInputProps={{ isLimited: false, license: license }}
          tabsPosition={"bottom"}
        />
      </div>
      {resultFile && (
        <BeatResult resultFile={resultFile} downloadFile={downloadResultFile} />
      )}
      <BeatSetting
        header={bpmSettingHeader}
        parameters={[
          {
            title: "Estimated BPM",
            value: bpmSettings.bpm,
            onValueChanged: setBpm,
            isDisabled: !bpmSettings.isManualTempo,
          },
          {
            title: "Tolerance",
            value: bpmSettings.tolerance,
            onValueChanged: setTolerance,
            isDisabled: !bpmSettings.isManualTempo,
          },
        ]}
        isValid={bpmSettings.isBpmValid && bpmSettings.isToleranceValid}
        validationErrors={bpmSettingValidationErrorMessages}
      />
      <div className={styles.settings_block}>
        <EffectsContainerHeader />
        {effects.map((effect, i) => (
          <BeatSetting
            key={i}
            header={
              <EffectSettingHeader
                index={i}
                title={effect.title}
                description={effect.description}
                changeEffectType={changeEffectType}
                removeEffect={removeEffect}
              />
            }
            parameters={effect.parameters.map((parameter) => ({
              title: parameter.title,
              value: parameter.value,
              onValueChanged: (value) =>
                updateEffectParameterValue(i, parameter.title, value),
            }))}
            isValid={effect.isValid}
            validationErrors={effect.errorMessages}
          />
        ))}
        <div className={styles.buttons_container}>
          <FilledButton
            text={"REMOVE EFFECT"}
            onClick={() => removeEffect(effects.length - 1)}
            disabled={!canRemoveLastEffect}
            backgroundColor={"#141414"}
            color={"#F9A825"}
            hoveringColor={"#F9A825"}
          />
          <FilledButton
            text={"ADD EFFECT"}
            onClick={addNewEffect}
            disabled={!canAddNewEffect}
            backgroundColor={"#FF5722"}
          />
        </div>
      </div>
      <FilledButton
        text={isLoading ? "Generating..." : "Generate"}
        onClick={isAvailableForUser ? applyEffects : showLicenseLimitModal}
        disabled={!canStartApplyingEffects || isLoading}
        backgroundColor={"#FF5722"}
      />
    </div>
  );
};

export default BeatsPage;
